import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    OutlinedInput,
    Checkbox,
    ListItemText,
    ListSubheader,
    FormControlLabel,
    Switch,
    Button,
} from '@mui/material';

import { DEFICIENCY_STATUSES, DEFERRED_COST_OPTIONS } from '../constants';

const ReportFilters = ({
    filters,
    onChange,
    businesses = [],
    floorplans = [],
    reportType,
    onGenerate,
    loading,
    showDeferredCost = true,
}) => {
    const [localFilters, setLocalFilters] = useState(filters);

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    // Group floorplans by groupName when any floorplan has a group
    const groupedFloorplanItems = useMemo(() => {
        const hasGroups = floorplans.some((f) => f.groupName);
        if (!hasGroups) return null;

        const groups = {};
        floorplans.forEach((f) => {
            const key = f.groupName || 'Ungrouped';
            if (!groups[key]) groups[key] = [];
            groups[key].push(f);
        });

        // Named groups alphabetically, Ungrouped last
        return Object.entries(groups).sort(([a], [b]) => {
            if (a === 'Ungrouped') return 1;
            if (b === 'Ungrouped') return -1;
            return a.localeCompare(b);
        });
    }, [floorplans]);

    const handleFilterChange = (field, value) => {
        const updatedFilters = { ...localFilters, [field]: value };
        setLocalFilters(updatedFilters);
        onChange(updatedFilters);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="subtitle2" fontWeight="600">
                Detailed Filters
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Status Filter - Hidden for Activity Summary, Company Performance, and Deferred Cost */}
                {reportType !== 'PERIOD_ACTIVITY' &&
                    reportType !== 'COMPANY_PERFORMANCE' &&
                    reportType !== 'DEFERRED_COST' && (
                        <Box>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    multiple
                                    value={localFilters.statuses || []}
                                    onChange={(e) => handleFilterChange('statuses', e.target.value)}
                                    input={<OutlinedInput label="Status" />}
                                    renderValue={(selected) => (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 0.5,
                                            }}
                                        >
                                            {selected.map((value) => (
                                                <Chip key={value} label={value} size="small" />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {DEFICIENCY_STATUSES.map((status) => (
                                        <MenuItem key={status} value={status}>
                                            <Checkbox
                                                checked={
                                                    localFilters.statuses?.includes(status) || false
                                                }
                                            />
                                            <ListItemText primary={status} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                {/* Company Filter - Hidden for Company Performance */}
                {businesses.length > 0 && reportType !== 'COMPANY_PERFORMANCE' && (
                    <Box>
                        <FormControl fullWidth size="small">
                            <InputLabel>Companies</InputLabel>
                            <Select
                                multiple
                                value={localFilters.assignedToBusinessIds || []}
                                onChange={(e) =>
                                    handleFilterChange('assignedToBusinessIds', e.target.value)
                                }
                                input={<OutlinedInput label="Companies" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((id) => {
                                            const business = businesses.find(
                                                (b) => b.businessId === id
                                            );
                                            return (
                                                <Chip
                                                    key={id}
                                                    label={business?.businessName || id}
                                                    size="small"
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            >
                                {businesses.map((business) => (
                                    <MenuItem key={business.businessId} value={business.businessId}>
                                        <Checkbox
                                            checked={
                                                localFilters.assignedToBusinessIds?.includes(
                                                    business.businessId
                                                ) || false
                                            }
                                        />
                                        <ListItemText primary={business.businessName} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}

                {/* Floorplan Filter - Hidden for Company Performance */}
                {floorplans.length > 0 && reportType !== 'COMPANY_PERFORMANCE' && (
                    <Box>
                        <FormControl fullWidth size="small">
                            <InputLabel>Floorplans</InputLabel>
                            <Select
                                multiple
                                value={localFilters.floorplanIds || []}
                                onChange={(e) => handleFilterChange('floorplanIds', e.target.value)}
                                input={<OutlinedInput label="Floorplans" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((id) => {
                                            const floorplan = floorplans.find(
                                                (f) => f.floorplanId === id
                                            );
                                            return (
                                                <Chip
                                                    key={id}
                                                    label={floorplan?.name || id}
                                                    size="small"
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            >
                                {groupedFloorplanItems
                                    ? groupedFloorplanItems.flatMap(
                                          ([groupLabel, groupFloorplans]) => [
                                              <ListSubheader
                                                  key={`group-${groupLabel}`}
                                                  sx={{
                                                      fontSize: '0.7rem',
                                                      fontWeight: 700,
                                                      textTransform: 'uppercase',
                                                      letterSpacing: '0.05em',
                                                      lineHeight: '28px',
                                                  }}
                                              >
                                                  {groupLabel}
                                              </ListSubheader>,
                                              ...groupFloorplans.map((floorplan) => (
                                                  <MenuItem
                                                      key={floorplan.floorplanId}
                                                      value={floorplan.floorplanId}
                                                      sx={{ pl: 4 }}
                                                  >
                                                      <Checkbox
                                                          checked={
                                                              localFilters.floorplanIds?.includes(
                                                                  floorplan.floorplanId
                                                              ) || false
                                                          }
                                                      />
                                                      <ListItemText primary={floorplan.name} />
                                                  </MenuItem>
                                              )),
                                          ]
                                      )
                                    : floorplans.map((floorplan) => (
                                          <MenuItem
                                              key={floorplan.floorplanId}
                                              value={floorplan.floorplanId}
                                          >
                                              <Checkbox
                                                  checked={
                                                      localFilters.floorplanIds?.includes(
                                                          floorplan.floorplanId
                                                      ) || false
                                                  }
                                              />
                                              <ListItemText primary={floorplan.name} />
                                          </MenuItem>
                                      ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}

                {/* Deferred Cost Filters - Hidden for Company Performance and Deferred Cost.
                    Also hidden for roles not permitted to see deferred cost (EXT-LAUNCH-009). */}
                {showDeferredCost &&
                    reportType !== 'COMPANY_PERFORMANCE' &&
                    reportType !== 'DEFERRED_COST' && (
                        <Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mb: 1, display: 'block' }}
                            >
                                Cost
                            </Typography>
                            <FormControl fullWidth size="small">
                                <InputLabel>Has Cost</InputLabel>
                                <Select
                                    value={localFilters.hasDeferredCost || 'ALL'}
                                    onChange={(e) =>
                                        handleFilterChange('hasDeferredCost', e.target.value)
                                    }
                                    label="Has Cost"
                                >
                                    {DEFERRED_COST_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                {/* Internal Flags - Hidden for Company Performance */}
                {reportType !== 'COMPANY_PERFORMANCE' && (
                    <Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 1, display: 'block' }}
                        >
                            Options
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={localFilters.includeInternalOnly !== false}
                                        onChange={(e) =>
                                            handleFilterChange(
                                                'includeInternalOnly',
                                                e.target.checked
                                            )
                                        }
                                        size="small"
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        Include &quot;internal only&quot; deficiencies
                                    </Typography>
                                }
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={localFilters.onlyInternallyCreated || false}
                                        onChange={(e) =>
                                            handleFilterChange(
                                                'onlyInternallyCreated',
                                                e.target.checked
                                            )
                                        }
                                        size="small"
                                    />
                                }
                                label={
                                    <Typography variant="body2">Only internally created</Typography>
                                }
                            />
                        </Box>
                    </Box>
                )}
            </Box>

            <Box sx={{ mt: 'auto', pt: 2 }}>
                <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={onGenerate}
                    disabled={loading}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': { boxShadow: 'none' },
                    }}
                >
                    {loading ? 'Generating...' : 'Generate Report'}
                </Button>
            </Box>
        </Box>
    );
};

export default ReportFilters;
